import { Button, Col, Grid, Row, Spinner } from 'gu-ui-kit'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Form, reduxForm } from 'redux-form'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import validator from 'utils/asyncValidator'
import { formatToString } from 'utils/formatMoney'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useQueryParams from 'utils/hooks/useQueryParams'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import PaymentProfilesControl from 'modules/Forms/SharedControls/PaymentProfilesControl'
import {
  Account,
  Amount,
  EmailDisapproved,
  ExchangeRate,
  PaymentOrderButton,
  Total,
  Wallets,
} from 'modules/Forms/Billing/subcomponents'
import { useBilling } from 'modules/Forms/Billing/useBilling'
import styles from 'modules/Forms/Billing/Billing-forms.module.scss'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { notificationShow } from 'modules/Notifications/actions'
import EmptyWalletNotification from 'modules/Forms/Billing/subcomponents/EmptyWalletNotification'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const validationSchema = Yup.object().shape({
  method: Yup.string().required('billing::errors.choose_payment_method'),
  payment_amount: Yup.object().shape({
    value: Yup.number()
      .typeError('billing::errors.required_field')
      .test({
        name: 'min_amount',
        test: function (value, ctx) {
          const { path, parent } = this
          const { min_amount = 0 } = ctx.from?.[1]?.value ?? {}

          if (!value) {
            return false
          }

          if (+value >= min_amount) {
            return true
          }

          return ctx.createError({
            path,
            message: i18n.t('billing::errors.specify_amount', {
              amount: formatToString(min_amount, parent.currency),
            }),
          })
        },
      })
      .required('billing::errors.required_field'),
    currency: Yup.string(),
  }),
  is_payment_profile: Yup.boolean(),
  payment_profile_id: Yup.string()
    .when('is_payment_profile', {
      is: true,
      then: (schema) => schema.required('billing::errors.required_field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  is_wallets: Yup.boolean(),
  wallet_id: Yup.string()
    .when('is_wallets', {
      is: true,
      then: (schema) => schema.required('billing::errors.required_field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
})

const CALCULATION_DELAY_TIME = 1000

let DepositCreationFeeForm = ({
  handleSubmit,
  form,
  reset,
  autofill,
  accountId = null,
  onClose = () => {},
}) => {
  const dispatch = useDispatch()
  const breakpoints = useBreakpoint()
  const { t } = useTranslation(['billing'])
  const {
    i18n,
    user: { is_payment_orders_creation_available = null, is_email_confirmed },
  } = useSelector(({ app }) => app)

  const { query, setQuery } = useQueryParams()
  const [formInitiated, setFormInitiated] = useState(false)
  const history = useHistory()
  const [redirectUrl, setRedirectUrl] = useState(null)
  const [summary, setSummary] = useState(null)

  const {
    data: {
      activeMethod,
      accountData: { loaded: accountDataLoaded, loading: accountDataLoading },
      networkAccountData: { type },
      accountAmount,
    } = {},
    calculate,
    topUp,
    values = {},
  } = useBilling(form)

  const [billingConfig, setBillingConfig] = useLocalStorage(form, {})

  const {
    payment_amount: { value: amount = null, currency = null } = {},
    is_payment_profile = false,
    payment_profile_id = null,
    method = null,
    is_wallets = false,
    wallet_id = null,
  } = values

  useEffect(() => {
    const { value = null } = summary?.deposit?.payment ?? {}
    if (accountAmount && value && +amount !== value) {
      autofill('payment_amount', value)
    }
  }, [summary, accountAmount])

  let windowReference = null

  const isGetuniqMethod = useMemo(
    () =>
      !activeMethod
        ? false
        : ['user_balance', 'user_referral_balance', 'cashless'].includes(activeMethod.method),
    [activeMethod],
  )

  const callTouch = useCallback(() => {
    if (IS_APP_PRODUCT_GETUNIQ || !accountId) {
      return
    }
    typeof window?.ct === 'function' && window.ct('3mtpffra', 'goal', 'replenishment')
  }, [accountId, IS_APP_PRODUCT_GETUNIQ])

  const submit = useCallback(
    (values) => {
      if (!isGetuniqMethod) {
        windowReference = window.open('', '_blank')
      }

      topUp({
        ...values,
        accountId,
        formName: form,
        networkType: type,
      })
        .then(({ redirect_url, pay_url }) => {
          if (isGetuniqMethod && redirect_url?.length > 0) {
            callTouch()
            location.href = redirect_url
          } else if (pay_url?.length > 0) {
            if (windowReference) {
              windowReference.location = pay_url
            } else {
              location.href = pay_url
            }
          }
          if (redirect_url?.length > 0) {
            setRedirectUrl(redirect_url)
          }
        })
        .catch(() => {
          if (windowReference) {
            windowReference.close()
          }
        })
        .finally(() => {
          const { method, payment_profile_id, wallet_id } = values
          setBillingConfig({ method, payment_profile_id, wallet_id })
        })
    },
    [
      type,
      accountId,
      type,
      setBillingConfig,
      isGetuniqMethod,
      setRedirectUrl,
      reset,
      form,
      windowReference,
    ],
  )

  useEffect(() => {
    global.getBack = () => {
      callTouch()
      reset()
      redirectUrl && history.push(redirectUrl)

      dispatch(
        notificationShow({
          type: 'success',
          message: t('billing::account_marketplace.notifications.success'),
        }),
      )

      global.getBack = undefined
      setRedirectUrl(null)
    }
  }, [redirectUrl])

  const initForm = useCallback(() => {
    const { method = null, payment_profile_id = null, wallet_id = null } = billingConfig // сохраненные в localstorage данные

    const activeMethodName = activeMethod.method
    const isCurrentBillingConfig = method === activeMethodName
    const isCurrentWallet =
      isCurrentBillingConfig &&
      activeMethod?.params?.wallets?.findIndex(({ id }) => id === wallet_id) > -1
    const isCurrentPaymentProfile =
      isCurrentBillingConfig &&
      activeMethod?.payment_profiles?.findIndex(({ id }) => id === payment_profile_id) > -1

    const isPaymentProfile = !!activeMethod?.payment_profiles
    const paymentProfileId = isPaymentProfile ? activeMethod?.payment_profiles?.[0]?.id : null

    const isWallets = !!activeMethod?.params?.wallets
    const firstWalletId = activeMethod?.params?.wallets?.[0]?.id

    const walletId = isWallets ? (firstWalletId === null ? 'null' : firstWalletId) : null

    autofill('method', activeMethodName)
    autofill('is_payment_profile', isPaymentProfile)
    autofill('payment_profile_id', isCurrentPaymentProfile ? payment_profile_id : paymentProfileId)
    autofill('is_wallets', !!activeMethod?.params?.wallets)
    autofill('wallet_id', isCurrentWallet ? wallet_id : walletId)

    setFormInitiated(true)
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod) {
      setQuery({ ...query, method: activeMethod.method })
      initForm()
    } else {
      reset()
    }
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod && accountDataLoaded) {
      initForm()
    }
  }, [activeMethod, accountDataLoaded])

  const isFormReady = useMemo(
    () =>
      method &&
      (!is_payment_profile || (is_payment_profile && payment_profile_id)) &&
      (!is_wallets || (is_wallets && wallet_id)) &&
      currency &&
      accountId,
    [values],
  )

  const runCalculate = useCallback(() => {
    calculate({
      accountId,
      ...omit(values, 'payment_amount'),
      currency: values?.payment_amount.currency,
      formName: form,
      delayTime: CALCULATION_DELAY_TIME,
    }).then(({ data }) => setSummary((summary) => ({ ...summary, ...data })))
  }, [accountId, values, form, CALCULATION_DELAY_TIME, setSummary])

  const resetCalculatedData = useCallback(() => {
    setSummary(null)
  }, [])

  // список кошельков
  const wallets = useMemo(
    () => activeMethod?.params?.wallets ?? null,
    [formInitiated, activeMethod, accountDataLoaded],
  )

  // платёжные профили
  const paymentProfiles = useMemo(
    () => activeMethod?.payment_profiles ?? null,
    [formInitiated, activeMethod],
  )

  // кошелек
  const wallet = useMemo(() => {
    if (wallet_id) {
      if (activeMethod?.method === 'user_referral_balance') {
        return activeMethod?.params.wallets?.[0]
      }

      return wallets?.find(({ id }) => id === wallet_id) || false
    }

    return false
  }, [formInitiated, wallet_id, wallets, accountDataLoaded, activeMethod])

  // спец валюта от способа пополнения
  const paymentProfileCurrency = useMemo(
    () =>
      (payment_profile_id &&
        paymentProfiles?.find(({ id }) => id === payment_profile_id)?.currency) ||
      false,
    [payment_profile_id, paymentProfiles, formInitiated],
  )

  // список валют
  // если нет спец валют используем валюты из списка валют
  // если нет ни того ни другого вообще не используем список
  const currencies = useMemo(
    () => (wallet?.currency || paymentProfileCurrency ? null : activeMethod?.currencies),
    [formInitiated, activeMethod, paymentProfileCurrency, wallet?.currency],
  )

  const currentCurrency = useMemo(
    () => paymentProfileCurrency || wallet?.currency || currencies?.[0]?.currency || null,
    [currencies, paymentProfileCurrency, wallet?.currency, formInitiated],
  )

  useEffect(
    () => autofill('payment_amount.currency', currentCurrency),
    [currentCurrency, currencies, activeMethod],
  )

  useEffect(
    () => calculate.finished && !isFormReady && resetCalculatedData(),
    [isFormReady, calculate.finished],
  )

  const canCreatePaymentOrderLink = useMemo(
    () =>
      !['user_balance', 'user_referral_balance', 'cashless', 'lucky'].includes(
        activeMethod?.method,
      ),
    [activeMethod],
  )

  useEffect(() => {
    if (summary?.deposit?.payment && !isEqual(values.payment_amount, summary?.deposit?.payment)) {
      autofill('payment_amount', summary?.deposit?.payment)
    }
  }, [summary, accountAmount])

  //more code duplication for god of duplication!
  const isAmountDisabled = useMemo(() => {
    switch (true) {
      case is_wallets && (!wallet || wallet?.balance === 0):
        return true
      case !is_wallets &&
        paymentProfiles !== null &&
        (paymentProfiles?.length === 0 || !payment_profile_id):
        return true
      default:
        return false
    }
  }, [is_wallets, wallet, paymentProfiles, payment_profile_id])

  useEffect(() => {
    if (!isFormReady) {
      return
    }
    resetCalculatedData()
    !isAmountDisabled && runCalculate()
  }, [isFormReady, values])

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <EmailDisapproved className={'margin-bottom-24'} />

      <PaymentProfilesControl
        disabled={!is_email_confirmed}
        className={styles['billing__row']}
        readOnly={calculate.progress || !paymentProfiles?.length}
        i18n={i18n}
        paymentProfiles={paymentProfiles}
        t={t}
        addUrlParams={{ payment_method: activeMethod?.method }}
      />
      <Account
        className={styles['billing__row']}
        t={t}
        i18n={i18n}
        disabled={!is_email_confirmed}
        {...activeMethod}
      />

      <EmptyWalletNotification
        className={'margin-bottom-24'}
        activeMethod={activeMethod}
        wallet={wallet}
      />

      <Wallets
        walletsLoading={accountDataLoading}
        className={styles['billing__row']}
        disabled={calculate.progress || !is_email_confirmed}
        wallets={wallets}
        t={t}
      />
      <Amount
        hidden
        readOnly
        name={'payment_amount.value'}
        currencyName={'payment_amount.currency'}
        className={styles['billing__row']}
        currencies={currencies}
        currency={currency}
        label={t('amount')}
        disabled={!is_email_confirmed || isAmountDisabled}
        t={t}
      />
      {calculate.finished && (
        <div className={styles['billing__deposit']}>
          <div className={styles['billing__calculated-data']}>
            <Grid>
              <Row>
                <Col
                  s={{ span: 12, order: 1 }}
                  sm={{ span: 12, order: 1 }}
                  lg={{ span: 12, order: 2 }}
                  className={styles['billing__total-with-commission']}
                >
                  {/* Итоговая сумма */}
                  <Total {...summary} />
                  <span className={styles['billing__total-without-commission']}>
                    <ExchangeRate {...summary} />
                  </span>
                </Col>
              </Row>
            </Grid>
          </div>

          {calculate.progress && (
            <div className={styles['billing__deposit-spinner']}>
              <Spinner color="Label-GU-Primary" />
            </div>
          )}
        </div>
      )}

      <Grid
        gap={16}
        direction={breakpoints.sm ? 'row' : 'column-reverse'}
        className={'margin-top-40'}
      >
        <Col
          className={styles['billing__buttons-reset']}
          s={{
            span: 12,
            order: 3,
          }}
          sm={{
            span: 6,
            order: 1,
          }}
        >
          <Button
            className={styles['billing__button']}
            type="button"
            size="l"
            onClick={onClose}
            variant={'ghost'}
            grey
          >
            {t('common::buttons.cancel')}
          </Button>
        </Col>
        <Col
          s={{
            span: 12,
            order: 1,
          }}
          sm={{
            span: 6,
            order: 2,
          }}
        >
          <Button
            className={styles['billing__button']}
            bgColor={calculate.finished ? 'Label-GU-Primary' : 'none'}
            type="submit"
            size="l"
            disabled={!calculate.finished || calculate.progress || topUp.progress || !summary}
            variant="primary"
          >
            {calculate.progress || topUp.progress
              ? t('loading')
              : t(activeMethod?.method === 'cashless' ? 'issue_invoice' : 'pay')}
          </Button>
        </Col>

        {canCreatePaymentOrderLink &&
          is_payment_orders_creation_available &&
          !calculate.progress &&
          calculate.finished && (
            <Col
              s={{
                span: 12,
                order: 2,
              }}
              sm={{ span: 12, order: 3 }}
            >
              <Grid justifyContent={'center'}>
                <PaymentOrderButton form={form} />
              </Grid>
            </Col>
          )}
      </Grid>
    </Form>
  )
}

DepositCreationFeeForm.propTypes = {
  handleSubmit: PropTypes.func,
  form: PropTypes.string,
  reset: PropTypes.func,
  accountId: PropTypes.string,
  autofill: PropTypes.func,
  focus: PropTypes.func,
  asyncValidate: PropTypes.func,
  blur: PropTypes.func,
  onClose: PropTypes.func,
  dirty: PropTypes.bool,
  valid: PropTypes.bool,
}

const DepositCreationFeeFormRedux = reduxForm({
  form: 'deposit_creation_fee',
  enableReinitialize: true,
  shouldAsyncValidate,
  touchOnBlur: false,
  touchOnChange: true,
  asyncValidate: validator(validationSchema),
})(DepositCreationFeeForm)

export default DepositCreationFeeFormRedux
